import { create } from 'zustand';
import { AvatarState, AvatarData } from '../types/store';
import UserService from '../service/UserService';

interface AvatarStore extends AvatarState {
  // Actions
  loadAvatar: (username: string) => Promise<void>;
  updateAvatar: (avatarData: AvatarData) => void;
  clearAvatar: () => void;
  reset: () => void;
}

const userService = new UserService();

export const useAvatarStore = create<AvatarStore>((set, get) => ({
  // Initial state
  avatarData: null,
  isLoaded: false,
  isLoading: false,
  error: null,

  // Actions
  loadAvatar: async (username: string) => {
    if (!username) {
      console.warn('🔍 Avatar Store - No username provided to loadAvatar');
      return;
    }

    // Don't reload if already loaded for this user
    const currentData = get().avatarData;
    if (get().isLoaded && currentData?.handle === username) {
      console.log('🔍 Avatar Store - Avatar already loaded for user:', username);
      return;
    }

    set({ isLoading: true, error: null });
    
    try {
      console.log('🔍 Avatar Store - Loading avatar for user:', username);
      const avatarData = await userService.getUserAvatar(username);
      
      console.log('🔍 Avatar Store - Avatar loaded successfully:', avatarData);
      set({ 
        avatarData, 
        isLoaded: true, 
        isLoading: false, 
        error: null 
      });
    } catch (error: any) {
      console.error('🔍 Avatar Store - Error loading avatar:', error);
      
      // If avatar data doesn't exist, create fallback with username as handle
      const fallbackAvatarData: AvatarData = {
        handle: username,
        bio: null,
        pronouns: null,
        location: null
      };
      
      console.log('🔍 Avatar Store - Using fallback avatar data:', fallbackAvatarData);
      set({ 
        avatarData: fallbackAvatarData, 
        isLoaded: true, 
        isLoading: false, 
        error: error.message || 'Failed to load avatar data'
      });
    }
  },

  updateAvatar: (avatarData: AvatarData) => {
    console.log('🔍 Avatar Store - Updating avatar data:', avatarData);
    set({ 
      avatarData, 
      isLoaded: true, 
      isLoading: false, 
      error: null 
    });
  },

  clearAvatar: () => {
    console.log('🔍 Avatar Store - Clearing avatar data - Stack trace:');
    console.trace();
    
    const currentState = get();
    console.log('🔍 Avatar Store - Current state before clearing:', {
      hasAvatarData: !!currentState.avatarData,
      isLoaded: currentState.isLoaded,
      isLoading: currentState.isLoading,
      hasError: !!currentState.error
    });
    
    set({
      avatarData: null,
      isLoaded: false,
      isLoading: false,
      error: null,
    });
    
    console.log('✅ Avatar Store - Avatar data cleared successfully');
  },

  reset: () => {
    console.log('🔍 Avatar Store - Resetting avatar store');
    set({ 
      avatarData: null, 
      isLoaded: false, 
      isLoading: false, 
      error: null 
    });
  },
}));
